import { AnimatePresence, motion } from "motion/react";
import { Fragment, useCallback } from "react";
import { type Control, useController, useFieldArray } from "react-hook-form";
import type { BidPostData } from "validations";

import { ControlledIntegerInput } from "@krea/shared/design-system/ControlledInputs/IntegerInput";
import CheckBox from "@krea/shared/design-system/fields/Checkbox";
import { PromotionTypeEnum } from "@krea/shared/types/common";

import DetailsItem from "../../details/item";

const LABEL_MAP: Record<PromotionTypeEnum, string> = {
  [PromotionTypeEnum.INTEREST_FREE]: "Interest free months",
  [PromotionTypeEnum.AMORTISATION_FREE]: "Amortisation free months",
};

interface IPromotionsSelectorProps {
  control: Control<BidPostData>;
  supportedPromotionTypes: PromotionTypeEnum[];
}

export const PromotionsSelector = ({
  supportedPromotionTypes,
  control,
}: IPromotionsSelectorProps) => {
  const {
    formState: { isSubmitting },
  } = useController({
    name: "promotions",
    control,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "promotions",
  });

  const getPromotionIndex = useCallback(
    (supportedPromotionType: PromotionTypeEnum) => {
      return fields.findIndex((item) => item.type === supportedPromotionType);
    },
    [fields],
  );

  return (
    <div>
      <ul className="list-none">
        {supportedPromotionTypes.map((supportedPromotionType) => {
          const typeIndex = getPromotionIndex(supportedPromotionType);
          const field = typeIndex > -1 ? fields[typeIndex] : null;

          return (
            <Fragment
              key={field ? field.id : `promotions-${supportedPromotionType}`}
            >
              <li className="mb-2">
                <CheckBox
                  id={`checkbox-${supportedPromotionType}`}
                  label={LABEL_MAP[supportedPromotionType]}
                  checked={!!field}
                  onChange={(checked) => {
                    if (checked) {
                      append({ type: supportedPromotionType });
                    } else if (field) {
                      remove(typeIndex);
                    }
                  }}
                  disabled={isSubmitting}
                />
              </li>

              <AnimatePresence>
                {field && (
                  <motion.div
                    initial={{
                      opacity: 0,
                      height: 0,
                    }}
                    animate={{
                      opacity: 1,
                      height: "auto",
                      transition: {
                        type: "tween",
                        duration: 0.1,
                        ease: "easeOut",
                      },
                    }}
                    exit={{
                      opacity: 0,
                      height: 0,
                      transition: {
                        type: "tween",
                        duration: 0.1,
                        ease: "easeOut",
                      },
                    }}
                  >
                    <DetailsItem className="mb-4 rounded border border-gray-300 p-4">
                      <div className="w-full">
                        <ControlledIntegerInput
                          label="Number of months the promotion refers to"
                          name={`promotions.${typeIndex}.value`}
                          control={control}
                          acceptsDecimals={false}
                          required={true}
                        />
                      </div>
                    </DetailsItem>
                  </motion.div>
                )}
              </AnimatePresence>
            </Fragment>
          );
        })}
      </ul>
    </div>
  );
};
